import { useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import { X } from "lucide-react";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle, preventAccidentalDismiss } from "@/components/ui/Modal";
import { ButtonShadcn as Button } from "@/components/ui/Button";
import RHFSelect from "@/components/form/RHFSelect";
import { CenteredSpinner } from "@/components/shared/Loader";
import { CLIENT_COPY, GENERIC_ERROR } from "@/lib/errorMessages";
import { FeedbackService } from "@/services/feedbackService";

const LISTS = [
  { name: "to", label: "To" },
  { name: "cc", label: "CC" },
  { name: "bcc", label: "BCC" },
];

// Each list holds SuperAdmin ids. The server's rule, in the server's words
// (CLIENT_COPY): mail goes out only with a To, so there is always one. Nobody
// can sit in two lists, because a SuperAdmin already picked in one is
// disabled in the other two.
const schema = z.object({
  to: z.array(z.string()).min(1, CLIENT_COPY.FEEDBACK_RECIPIENTS_TO_REQUIRED),
  cc: z.array(z.string()),
  bcc: z.array(z.string()),
});

const ids = (people) => (people ?? []).map((p) => p.id);

/**
 * Who is mailed about every feedback a tenant user sends: To, CC and BCC.
 * Only SuperAdmins — each list picks from the active ones the server offers,
 * and the mail goes to each one's current address. BCC recipients are never
 * shown to the others.
 */
const EmailSettingsModal = ({ onClose }) => {
  const methods = useForm({ resolver: zodResolver(schema), defaultValues: { to: [], cc: [], bcc: [] } });
  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;
  const picked = useWatch({ control });
  // "loading", "ready" or "failed".
  const [load, setLoad] = useState("loading");
  const [candidates, setCandidates] = useState([]);

  useEffect(() => {
    let live = true;
    FeedbackService.recipients()
      .then((res) => {
        if (!live) return;
        setCandidates(res?.data?.candidates ?? []);
        reset({ to: ids(res?.data?.to), cc: ids(res?.data?.cc), bcc: ids(res?.data?.bcc) });
        setLoad("ready");
      })
      .catch(() => live && setLoad("failed"));
    return () => {
      live = false;
    };
  }, [reset]);

  // One list's options: every candidate, with those already in another list
  // disabled — still shown, so the reader sees why they cannot be picked here.
  const optionsFor = (name) => {
    const elsewhere = new Set(LISTS.filter((l) => l.name !== name).flatMap((l) => picked?.[l.name] ?? []));
    return candidates.map((p) => ({ value: p.id, label: `${p.name} (${p.email})`, disabled: elsewhere.has(p.id) }));
  };

  const onSubmit = async (values) => {
    try {
      await FeedbackService.updateRecipients(values);
      toast.success("Email settings saved.");
      onClose?.();
    } catch (err) {
      toast.error(err?.userMessage || GENERIC_ERROR);
    }
  };

  const close = () => !isSubmitting && onClose?.();

  return (
    <Dialog open onOpenChange={preventAccidentalDismiss(close)}>
      <DialogContent className="w-[340px] md:w-[560px] flex flex-col">
        <DialogHeader className="relative pl-5 pr-14 py-3 border-b border-border-primary shrink-0">
          <DialogTitle className="text-[15px] font-semibold text-text-primary">Email settings</DialogTitle>
          <DialogDescription className="text-[12px] text-text-secondary mt-0.5">Which SuperAdmins receive an email when someone sends feedback.</DialogDescription>
          <DialogClose
            aria-label="Close"
            disabled={isSubmitting}
            className="absolute right-4 top-3 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center"
          >
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)} noValidate>
            <div className="px-5 py-4 space-y-5 max-h-[65vh] overflow-y-auto">
              {load === "loading" && <CenteredSpinner />}
              {load === "failed" && <p className="text-sm text-status-error">Couldn't load the email settings. Close this and try again.</p>}
              {load === "ready" &&
                LISTS.map((list) => (
                  <RHFSelect
                    key={list.name}
                    name={list.name}
                    label={list.label}
                    placeholder="Pick SuperAdmins"
                    options={optionsFor(list.name)}
                    multiple
                    chips
                    searchable
                    // readOnly, not disabled: a disabled field is dropped from
                    // what react-hook-form submits, and Save would send nothing.
                    readOnly={isSubmitting}
                  />
                ))}
            </div>

            <div className="border-t border-border-primary px-5 py-3 flex justify-end gap-2">
              <Button variant="tertiary" type="button" onClick={close} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" variant="secondary" disabled={isSubmitting || load !== "ready"}>
                {isSubmitting ? "Saving…" : "Save"}
              </Button>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default EmailSettingsModal;
